/**
 * Modal para emitir NF-e de devolução com seleção de itens.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { AlertCircle, CheckCircle, Loader2, Package } from 'lucide-react'
import ModalERP from '../../ModalERP'
import { saleReturnService } from '../../../../services/erpApi'
import './EmitirDevolucaoModal.css'

function formatCurrency(value) {
  const num = parseFloat(value) || 0
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function EmitirDevolucaoModal({ isOpen, onClose, venda, onSuccess }) {
  const [loading, setLoading] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [motivo, setMotivo] = useState('')
  const [preview, setPreview] = useState(null)
  const [selected, setSelected] = useState({})

  const resetState = useCallback(() => {
    setLoading(false)
    setLoadingPreview(false)
    setError(null)
    setSuccess(null)
    setMotivo('')
    setPreview(null)
    setSelected({})
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetState()
      return
    }

    const load = async () => {
      if (!venda?.id) return
      setLoadingPreview(true)
      setError(null)
      try {
        const res = await saleReturnService.getPreview(venda.id)
        if (res.success) {
          setPreview(res.data)
          const initial = {}
          for (const item of res.data.items || []) {
            if (item.quantity_available > 0) {
              initial[item.sale_item_id] = {
                checked: false,
                quantity: item.quantity_available
              }
            }
          }
          setSelected(initial)
        } else {
          setError(res.message || 'Não foi possível carregar itens da devolução')
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Erro ao carregar preview')
      } finally {
        setLoadingPreview(false)
      }
    }

    load()
  }, [isOpen, venda?.id, resetState])

  const selectedLines = useMemo(() => {
    if (!preview?.items) return []
    return preview.items
      .filter((item) => selected[item.sale_item_id]?.checked)
      .map((item) => {
        const sel = selected[item.sale_item_id]
        const qty = Math.min(
          parseInt(sel.quantity, 10) || 0,
          item.quantity_available
        )
        return { ...item, returnQty: qty }
      })
      .filter((line) => line.returnQty > 0)
  }, [preview, selected])

  const totalParcial = useMemo(
    () => selectedLines.reduce((s, l) => s + l.returnQty * l.unit_price, 0),
    [selectedLines]
  )

  const isPartial = useMemo(() => {
    if (!preview?.items?.length || !selectedLines.length) return false
    const allSelected = preview.items
      .filter((i) => i.quantity_available > 0)
      .every((i) => selected[i.sale_item_id]?.checked)
    const allFull = selectedLines.every(
      (l) => l.returnQty === l.quantity_available
    )
    return !(allSelected && allFull)
  }, [preview, selectedLines, selected])

  const toggleItem = (saleItemId) => {
    setSelected((prev) => {
      const current = prev[saleItemId] || { checked: false, quantity: 1 }
      return {
        ...prev,
        [saleItemId]: { ...current, checked: !current.checked }
      }
    })
  }

  const setItemQty = (saleItemId, qty, max) => {
    const parsed = Math.max(1, Math.min(max, parseInt(qty, 10) || 1))
    setSelected((prev) => ({
      ...prev,
      [saleItemId]: { ...(prev[saleItemId] || {}), quantity: parsed, checked: true }
    }))
  }

  const selectAll = () => {
    if (!preview?.items) return
    const next = { ...selected }
    for (const item of preview.items) {
      if (item.quantity_available > 0) {
        next[item.sale_item_id] = {
          checked: true,
          quantity: item.quantity_available
        }
      }
    }
    setSelected(next)
  }

  const handleSubmit = async () => {
    if (!selectedLines.length) {
      setError('Selecione ao menos um item com quantidade válida')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const items = selectedLines.map((l) => ({
        sale_item_id: l.sale_item_id,
        quantity: l.returnQty
      }))

      const res = await saleReturnService.emitir(venda.id, { items, motivo: motivo.trim() || undefined })

      if (res.success && res.data?.success !== false) {
        setSuccess('Devolução concluída com sucesso.')
        onSuccess?.(res.data)
      } else if (res.data?.canRetryFiscal) {
        setError(
          res.message ||
            'Estoque atualizado, mas a NF falhou. Use "Reenviar nota fiscal" no menu da venda.'
        )
        onSuccess?.(res.data)
      } else {
        setError(res.message || 'Falha ao processar devolução')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao emitir devolução')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  return (
    <ModalERP
      isOpen={isOpen}
      onClose={handleClose}
      title="Emitir devolução (NF-e)"
      size="lg"
    >
      <div className="emitir-devolucao-modal">
        {loadingPreview && (
          <div className="emitir-devolucao-loading">
            <Loader2 className="spin" size={24} />
            <span>Carregando itens...</span>
          </div>
        )}

        {!loadingPreview && preview && !preview.can_emit && (
          <div className="emitir-devolucao-alert warning">
            <AlertCircle size={18} />
            <span>
              Esta venda não está elegível para devolução fiscal no momento.
              {!preview.original_authorized && (
                <>
                  {' '}
                  Nota original não autorizada
                  {preview.original_fiscal_status
                    ? ` (status: ${preview.original_fiscal_status})`
                    : ''}
                  .
                </>
              )}
              {preview.active_return && ' Já existe devolução em andamento.'}
              {!preview.items?.some((i) => i.quantity_available > 0) &&
                ' Não há itens com saldo para devolver.'}
            </span>
          </div>
        )}

        {!loadingPreview && preview?.items?.length > 0 && (
          <>
            <div className="emitir-devolucao-toolbar">
              <button type="button" className="link-btn" onClick={selectAll}>
                Selecionar todos com saldo
              </button>
              {isPartial && (
                <span className="emitir-devolucao-partial-hint">
                  Devolução parcial — a NF referenciará apenas os itens selecionados
                </span>
              )}
            </div>

            <div className="emitir-devolucao-table-wrap">
              <table className="emitir-devolucao-table">
                <thead>
                  <tr>
                    <th />
                    <th>Produto</th>
                    <th>SKU</th>
                    <th>Vendida</th>
                    <th>Já devolvida</th>
                    <th>Qtd devolver</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => {
                    const disabled = item.quantity_available <= 0
                    const sel = selected[item.sale_item_id] || {}
                    const checked = !!sel.checked
                    const qty = sel.quantity ?? item.quantity_available

                    return (
                      <tr
                        key={item.sale_item_id}
                        className={disabled ? 'disabled' : checked ? 'selected' : ''}
                        onClick={() => !disabled && toggleItem(item.sale_item_id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            disabled={disabled || loading}
                            checked={checked}
                            onChange={() => toggleItem(item.sale_item_id)}
                          />
                        </td>
                        <td>{item.product_name}</td>
                        <td>{item.sku || '—'}</td>
                        <td>{item.quantity_sold}</td>
                        <td>{item.quantity_already_returned}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number"
                            min={1}
                            max={item.quantity_available}
                            disabled={disabled || !checked || loading}
                            value={checked ? qty : ''}
                            onChange={(e) =>
                              setItemQty(item.sale_item_id, e.target.value, item.quantity_available)
                            }
                            className="qty-input"
                          />
                        </td>
                        <td>
                          {checked
                            ? formatCurrency(qty * item.unit_price)
                            : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="emitir-devolucao-footer">
              <div className="emitir-devolucao-total">
                <Package size={18} />
                <strong>Total selecionado:</strong> {formatCurrency(totalParcial)}
              </div>

              <label className="emitir-devolucao-motivo">
                Motivo (opcional)
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={2}
                  placeholder="Ex.: arrependimento do cliente"
                  disabled={loading}
                />
              </label>
            </div>
          </>
        )}

        {error && (
          <div className="emitir-devolucao-alert error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="emitir-devolucao-alert success">
            <CheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        <div className="emitir-devolucao-actions">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={loading}>
            Fechar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              loadingPreview ||
              !preview?.can_emit ||
              !selectedLines.length
            }
          >
            {loading ? <Loader2 className="spin" size={18} /> : null}
            Confirmar devolução
          </button>
        </div>
      </div>
    </ModalERP>
  )
}

export default EmitirDevolucaoModal
